import { z } from 'zod';
/**
 * **Contrats d'entrée du chat — `CDC-3` F-39 à F-45.**
 *
 * Écrits une fois ici, ils valident l'entrée de l'API et valideraient les mêmes
 * formulaires côté front sans être réécrits (`DOCTRINE.md` §4). `strictObject` partout :
 * une clé inconnue produit un 400 visible.
 *
 * **CE QUE CE FICHIER NE PORTE PAS, ET IL FAUT LE LIRE.** Il n'y a **aucun contrat de
 * création de conversation**, et ce n'est pas un oubli : « une conversation existe dès que
 * la relation existe, **sans geste**. Personne ne la crée, personne ne la démarre »
 * (DD-25). Le geste « nouvelle conversation » n'existe pas, exactement comme « nouvelle
 * page » n'existe pas sur le tableau. Elle naît au provisionnement d'une relation.
 *
 * **Il n'y a pas non plus de contrat de suppression** : « une conversation ne se supprime
 * pas. Aucun geste, d'aucun rôle, ne détruit une conversation » (DD-29). Ce qui se retire
 * est un message, par son auteur.
 */
/** Identifiant opaque d'une ligne. Jamais interprété côté client. */
const identifiant = z.string().trim().min(1).max(64);
/**
 * **Le texte d'un message. AUCUNE LONGUEUR MAXIMUM** (DD-26).
 *
 * La borne écrite ici n'est **pas** une borne métier : elle empêche qu'un corps de requête
 * occupe le processus, au même titre que la borne du parser sur un fichier. Elle est haute
 * pour ne rien refuser que quelqu'un écrirait vraiment.
 */
const texteDeMessage = z
    .string()
    // **`trim` AVANT la borne basse, et c'est un vrai refus** : sans lui, un texte
    // d'espaces passe et produit un message qui a l'air vide sans l'être — donc un
    // message que la dérivation du retrait ne reconnaîtrait pas comme retiré, et qui
    // n'afficherait rien. Mesuré, pas supposé.
    .trim()
    .min(1)
    .max(100_000);
// ---------------------------------------------------------------------------
// F-40, F-41 — le message
// ---------------------------------------------------------------------------
/**
 * `POST /api/v1/conversations/:id/messages` — F-40 et F-41.
 *
 * **ENVOI MULTIPART, ET C'EST L'ISSUE C′ DE D-052.** Le contrat portait auparavant
 * `fichiers`, « un tableau d'identifiants de fichier **déjà déposé** » — et **c'était une
 * impossibilité qu'aucun test ne révélait** : un fichier déjà déposé vit dans un dossier
 * ordinaire de son envoyeur, jamais dans le dossier tenu par le produit, où F-41 veut
 * qu'il se range et où **aucune route ne peut déposer**. Le corps devient donc multipart :
 * les octets voyagent à côté de ce schéma, et **le produit les range lui-même**.
 *
 * **Ce schéma ne porte donc que le texte**, et le texte est **facultatif** : « un message
 * peut porter un ou plusieurs fichiers », donc un message de fichiers seuls existe. Ce qui
 * n'existe pas est un message **vide** — « un message ne naît jamais vide », et c'est ce
 * qui rend le retrait dérivable.
 */
export const envoiMessageSchema = z.strictObject({
    texte: texteDeMessage
        .optional()
        .describe('Le texte du message. Aucune longueur maximum au cahier des charges. ' +
        'FACULTATIF : un message peut ne porter que des fichiers. Mais un message ' +
        'ne naît jamais vide — sans texte ni fichier, l’envoi est refusé.'),
});
/**
 * `PATCH /api/v1/messages/:id` — F-40, **auteur seul, sous cinq minutes**.
 *
 * **Le texte est obligatoire ici alors qu'il est facultatif à l'envoi**, et l'asymétrie
 * est voulue : corriger, c'est remplacer un texte. Vider un message n'est pas une
 * correction, c'est un **retrait**, et il a sa propre route.
 *
 * **Aucun champ de fichier** : « la correction réécrit le texte sur place », et le corpus
 * ne donne aucun geste pour changer les fichiers d'un message envoyé.
 */
export const correctionMessageSchema = z.strictObject({
    texte: texteDeMessage.describe('Le nouveau texte. Le texte d’avant N’EST PAS CONSERVÉ, et la conversation ' +
        'montre seulement QU’IL a été corrigé. Passé cinq minutes, le texte ne se ' +
        'change plus.'),
});
/**
 * **La liste fermée des réactions, tenue par le produit.**
 *
 * **DÉRIVATION DE CONSTRUCTION, SIGNALÉE COMME TELLE.** F-40 et DD-27 exigent « une liste
 * fermée **tenue par le produit**, jamais un texte libre » et **ne donnent aucune
 * valeur** : un lecteur qui chercherait ces caractères au cahier des charges ne les y
 * trouverait pas.
 *
 * **Elle vit dans le contrat et non dans un type énuméré en base**, et c'est ce que
 * « tenue par le produit » veut dire : un ajout est un changement de contrat, jamais une
 * migration.
 */
export const REACTIONS = ['👍', '❤️', '😂', '😮', '😢', '🙏'];
/**
 * `PUT /api/v1/messages/:id/reaction` — F-40, **une par personne et par message**.
 *
 * **`PUT` et non `POST`, parce que le geste REMPLACE** : la contrainte d'unicité de la
 * base porte désormais sur la paire message-personne (D-051), donc une seconde réaction
 * de la même personne prend la place de la première au lieu de s'ajouter.
 *
 * **`valeur` vide RETIRE la réaction, et c'est une dérivation de construction.** Le corpus
 * ne dit pas qu'on peut la retirer ; il ne dit pas non plus le contraire, et une réaction
 * posée par erreur qu'on ne pourrait jamais reprendre serait un piège. Le retrait passe
 * par le même geste, ce qui évite une route de plus.
 */
export const reactionSchema = z.strictObject({
    valeur: z
        .enum(REACTIONS)
        .nullable()
        .describe('La réaction, choisie dans la liste fermée tenue par le produit — jamais un ' +
        'texte libre. UNE SEULE par personne et par message : celle-ci remplace la ' +
        'précédente. VIDE la retire.'),
});
// ---------------------------------------------------------------------------
// F-42 — l'état de vue et la préférence de notification
// ---------------------------------------------------------------------------
/**
 * `PUT /api/v1/conversations/:id/lecture` — F-42.
 *
 * **L'état de vue est UN SEUL CHAMP, le rang du dernier message lu**, et « le compte des
 * non-lus et l'indicateur de lecture s'en dérivent tous les deux ». Il n'y a donc rien
 * d'autre à envoyer, et surtout **aucun instant** : « l'autre voit qu'on a lu, sans
 * instant — jamais *lu à 18h04* ».
 */
export const lectureSchema = z.strictObject({
    rang: z
        .number()
        .int()
        .min(0)
        .describe('Le rang du dernier message lu. IL NE RECULE JAMAIS : un rang inférieur à ' +
        'celui déjà posé est ignoré, sans quoi des non-lus ressusciteraient et ' +
        'l’indicateur de lecture de l’autre se retournerait.'),
});
/**
 * `PUT /api/v1/conversations/:id/notification` — F-42 et `CDC-4`.
 *
 * **C'est une EXCEPTION qui surcharge une préférence, jamais la préférence elle-même**
 * (Q486) : la préférence générale vit sur la personne, et celle-ci ne vaut que pour cette
 * conversation. **Vide rend la conversation à la préférence générale**, ce qui est la
 * seule façon de défaire l'exception sans en poser une autre.
 */
export const notificationSchema = z.strictObject({
    notifier: z
        .boolean()
        .nullable()
        .describe('Exception pour CETTE conversation seulement. Vrai ou faux surcharge la ' +
        'préférence générale ; VIDE retire l’exception et rend la conversation à ' +
        'cette préférence.'),
});
// ---------------------------------------------------------------------------
// F-40 — la pagination du fil
// ---------------------------------------------------------------------------
/**
 * `GET /api/v1/conversations/:id/messages` — F-40, **pagination par curseur**.
 *
 * **LE CURSEUR EST LE RANG, ET C'EST CE QUI ÉVITE ICI L'ÉCART QUE LE DRIVE A DÛ
 * SIGNALER.** `DOCTRINE.md` §5 impose une pagination par curseur ; le Drive n'a pas pu la
 * tenir, son arborescence étant de profondeur illimitée et sans borne de volumétrie au
 * corpus. Un fil, lui, a un ordre total et une clé unique par conversation — le rang —
 * donc le curseur existe naturellement.
 *
 * **On remonte le fil vers le passé** : sans curseur on obtient les plus récents, et
 * `avant` demande ce qui précède.
 */
export const filQuerySchema = z.strictObject({
    avant: z.coerce
        .number()
        .int()
        .min(0)
        .optional()
        .describe('Le curseur : ne rendre que les messages de rang STRICTEMENT inférieur. ' +
        'Absent, on obtient les plus récents.'),
    combien: z.coerce
        .number()
        .int()
        .min(1)
        .max(100)
        .optional()
        .describe('Combien de messages au plus. Cinquante par défaut.'),
});
/** Réexporté pour les routes qui prennent un identifiant en chemin. */
export const identifiantDeLigne = identifiant;
//# sourceMappingURL=conversations.contract.js.map